'use client';
import { getColorClassNameAndStyle } from '@unifyapps/carbon/components/ColorPicker/utils/getColorClassNameAndStyle';
import HtmlContent from '@unifyapps/carbon/components/HtmlContent';
import type { HTMLContentProps } from '@unifyapps/carbon/components/HtmlContent/types';
import type { MarkdownWithExpandCollapseType } from '@unifyapps/carbon/components/Typography/MarkdownWithExpandCollapse';
import { MarkdownWithExpandCollapseSlots } from '@unifyapps/carbon/components/Typography/MarkdownWithExpandCollapse/types';
import type { TypographyWithExpandCollapseType } from '@unifyapps/carbon/components/Typography/TypographyWithExpandCollapse';
import { TypographyWithExpandCollapseSlots } from '@unifyapps/carbon/components/Typography/TypographyWithExpandCollapse';
import { TypographyWithTooltip as BaseTypographyWithTooltip } from '@unifyapps/carbon/components/Typography/TypographyWithTooltip';
import { dynamic } from '@unifyapps/carbon/external/dynamic';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import { useTextAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useTextAppearanceStyles';
import { isEmptyBlock } from '@unifyapps/carbon/no-code/utils/block';
import { getCustomVariantDataAttributes } from '@unifyapps/carbon/no-code/utils/getCustomVariantDataAttributes';
import {
  getStringifiedValue,
  getValueByDataType,
} from '@unifyapps/carbon/no-code/utils/typography';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import type {
  TypographyBlockStateType,
  TypographyComponentType,
} from '@unifyapps/defs/blocks/Typography/types';
import { Link } from '@unifyapps/ui/components/Link';
import {
  Typography as BaseTypography,
  type TypographyProps,
} from '@unifyapps/ui/components/Typography';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { useSlot } from '@unifyapps/ui/slots';
import type { CreateSlotsAndSlotProps, SlotProps } from '@unifyapps/ui/slots/types';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { clsx } from 'clsx';
import _isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import React, { memo, useMemo } from 'react';
import { useBlockTooltip } from '../common/hooks/useBlockTooltip';
import { MARKDOWN_CLASSNAMES } from './const/proseClassNames';
import { resolveTypographyType } from './resolveTypographyType';
import { weightVariants } from './style';

/** all known Typography Appearance keys — anything else is a custom variant dimension */
const KNOWN_APPEARANCE_KEYS = new Set([
  'variant',
  'color',
  'weight',
  'align',
  'styles',
  'preserveNewline',
  'visibility',
  'className',
  'customStyles',
]);

const BaseMarkdown = dynamic(
  () =>
    import(
      /* webpackChunkName: "block-markdown" */
      './Markdown'
    ),
);

const TypographyWithExpandCollapse = dynamic(
  () =>
    import(
      /* webpackChunkName: "block-TypographyWithExpandCollapse" */
      '@unifyapps/carbon/components/Typography/TypographyWithExpandCollapse/TypographyWithExpandCollapse'
    ),
) as unknown as TypographyWithExpandCollapseType;

const MarkdownWithExpandCollapse = dynamic(
  () =>
    import(
      /* webpackChunkName: "block-MarkdownWithExpandCollapse" */
      '@unifyapps/carbon/components/Typography/MarkdownWithExpandCollapse'
    ),
) as unknown as MarkdownWithExpandCollapseType;


const LINK_STYLES = { textDecorationColor: 'var(--palette-blue-dark-800)' };

const TYPOGRAPHY_VS_PROSE_P_VARIANTS = {
  //class !text-md/!text-xs do not work when the default class prose-p:text-sm is present, hence using the below condition
  'text-md': 'prose-p:text-md',
  'text-xs': 'prose-p:text-xs',
  // use para styles for p tags when variant is para-xs, para-sm, para-xmd, para-md
  'para-md': 'prose-p:para-md',
  'para-xmd': 'prose-p:para-xmd',
  'para-sm': 'prose-p:para-sm',
  'para-xs': 'prose-p:para-xs',
};

type TypographyBlockProps = BlockComponentProps<
  TypographyComponentType,
  TypographyBlockStateType,
  TypographyBlockStateType,
  HTMLElement
> &
  CreateSlotsAndSlotProps<
    {
      markdown?: React.ComponentType<{
        children: string;
      }>;
      html?: React.ComponentType<HTMLContentProps>;
    },
    {
      markdown: SlotProps<
        React.ComponentType<{
          children: string;
        }>,
        object,
        object
      >;
      html: SlotProps<React.ComponentType<Partial<HTMLContentProps>>, object, object>;
    }
  > &
  Pick<TypographyProps, 'dir'> & {
    markdownClassName?: string;
  };

function TypographyBlock(props: TypographyBlockProps) {
  const {
    attributesAndEventListeners,
    computedBlockState,
    component,
    dndRef,
    block,
    registry,
    dir,
    markdownClassName,
  } = props;
  const BlockRenderer = registry.getBlockRenderer();
  const {
    content: { value: _value, type, addOns },
    appearance,
  } = computedBlockState;

  const { slots } = component;

  const value = useMemo(() => getValueByDataType(_value), [_value]);
  const resolvedType = resolveTypographyType(type);

  const {
    enableTruncation,
    truncateWith,
    maxNumberOfLines,
    showTooltipOnTextOverflow,
    tooltipPlacement = 'top',
  } = addOns?.truncation ?? {};

  const {
    color,
    variant,
    weight,
    align,
    fontFamily,
    fontStyle,
    textDecoration,
    textTransform,
    lineHeight,
    letterSpacing,
  } = appearance ?? {};

  const { textClassName, textStyle } = useTextAppearanceStyles({
    fontFamily,
    fontStyle,
    textDecoration,
    textTransform,
    lineHeight,
    letterSpacing,
  });

  const { className: colorClassName, style: colorStyle } = useMemo(
    () => getColorClassNameAndStyle(color, 'color'),
    [color],
  );

  const { className, style } = useBlockAppearanceStyles({ appearanceStyles: appearance?.styles });

  const { className: maskClassName, style: maskStyle } = useBlockAppearanceStyles({
    appearanceStyles: { backgroundColor: style?.backgroundColor },
  });

  const { finalStyle } = useCustomStyles({
    customCSS: block.additional?.customCSS,
    customStyles: block.component?.appearance?.customStyles,
    style,
  });

  // custom variant dimensions (e.g. radius → data-radius="pill") applied as data attributes
  const customVariantDataAttrs = useMemo(
    () =>
      getCustomVariantDataAttributes(
        appearance as Record<string, unknown> | undefined,
        KNOWN_APPEARANCE_KEYS,
      ),
    [appearance],
  );

  const typographyProps = {
    align,
    maxNumberOfLines: enableTruncation ? maxNumberOfLines : undefined,
    style: {
      ...colorStyle,
      ...finalStyle,
      ...textStyle,
    },
    variant,
    weight,
    dir,
    ...customVariantDataAttrs,
    ...attributesAndEventListeners,
  };

  const Typography =
    enableTruncation && showTooltipOnTextOverflow && maxNumberOfLines
      ? BaseTypographyWithTooltip
      : BaseTypography;

  const [Markdown] = useSlot('markdown', {
    elementType: BaseMarkdown,
    externalForwardedProps: {
      slots: props.slots,
      slotProps: props.slotProps,
    },
    ownerState: {},
    className: '',
  });

  const [HtmlRenderer, htmlProps] = useSlot('html', {
    elementType: HtmlContent,
    externalForwardedProps: { slots: props.slots, slotProps: props.slotProps },
    ownerState: {},
    className: clsx(className, colorClassName, textClassName),
    ref: dndRef,
    additionalProps: {
      value,
      ...omit(typographyProps, ['maxNumberOfLines']),
      autofocus: false,
      isPlainHTML: resolvedType === 'HTML',
    },
  });

  const tooltipSlotBlockId = slots?.tooltip?.blockId;

  const plainText = (
    <Typography
      // Newline-preservation is always enabled for the Typography block.
      className={clsx(className, colorClassName, textClassName, {
        'whitespace-pre-line': maxNumberOfLines !== 1,
      })}
      {...typographyProps}
      placement={tooltipPlacement}
      ref={dndRef}
    >
      {getStringifiedValue(value)}
    </Typography>
  );

  const { wrapWithTooltip, tooltipBuilderNode } = useBlockTooltip({
    blockId: computedBlockState.id,
    tooltipAddOn: addOns?.tooltip,
    tooltipSlotBlockId,
    BlockRenderer,
  });

  if (resolvedType === 'MARKDOWN' && !_isNil(value)) {
    const isLabelTruncation =
      enableTruncation && truncateWith === 'label' && maxNumberOfLines && !isNative();

    const composedMarkdownClassName = slowCn(
      MARKDOWN_CLASSNAMES,
      className,
      colorClassName,
      weightVariants({ weight }),
      variant ? (TYPOGRAPHY_VS_PROSE_P_VARIANTS[variant] ?? 'prose-p:text-sm') : 'prose-p:text-sm',
      // to handled `/n` in markdown
      // breaking for `\n\n1. type lists items`
      // newlines are always preserved; only single-line truncation suppresses the wrap
      { 'whitespace-pre-line': maxNumberOfLines !== 1 && !isLabelTruncation },
      /**
       * The markdown may contain various tags that may have different line heights,
       * The max-height of typography is computed based on the variant line height (ex: for text-md line height is var(--lineHeight-text-md))
       * so the content may get truncated before the max number of lines is reached
       * so we need to unset the max-height to avoid this
       */
      '!max-h-[unset]',
      markdownClassName,
    );
    const markdownSource = getStringifiedValue(value);

    if (isLabelTruncation) {
      const collapsedLabel =
        slots?.collapsedTypography?.blockId && !isEmptyBlock(slots.collapsedTypography.blockId) ? (
          <Link style={LINK_STYLES}>
            <BlockRenderer
              blockId={slots.collapsedTypography.blockId}
              key={slots.collapsedTypography.blockId}
              parentBlockId={computedBlockState.id}
            />
          </Link>
        ) : null;
      const expandedLabel =
        slots?.expandedTypography?.blockId && !isEmptyBlock(slots.expandedTypography.blockId) ? (
          <Link style={LINK_STYLES}>
            <BlockRenderer
              blockId={slots.expandedTypography.blockId}
              key={slots.expandedTypography.blockId}
              parentBlockId={computedBlockState.id}
            />
          </Link>
        ) : null;

      return (
        <React.Suspense fallback={null}>
          {wrapWithTooltip(
            <MarkdownWithExpandCollapse
              align={align}
              className={composedMarkdownClassName}
              dir={dir}
              maxNumberOfLines={maxNumberOfLines}
              ref={dndRef}
              rootProps={{ ...customVariantDataAttrs, ...attributesAndEventListeners }}
              style={typographyProps.style}
              text={markdownSource}
              toggleMaskClassName={maskClassName}
              toggleMaskStyle={maskStyle}
              variant={variant}
              weight={weight}
            >
              <MarkdownWithExpandCollapse.Slot name={MarkdownWithExpandCollapseSlots.Collapse}>
                {collapsedLabel}
              </MarkdownWithExpandCollapse.Slot>
              <MarkdownWithExpandCollapse.Slot name={MarkdownWithExpandCollapseSlots.Expand}>
                {expandedLabel}
              </MarkdownWithExpandCollapse.Slot>
            </MarkdownWithExpandCollapse>,
          )}
          {tooltipBuilderNode}
        </React.Suspense>
      );
    }

    // Rendered as 'div' to prevent hydration error
    return (
      <>
        {wrapWithTooltip(
          <Typography
            as="div"
            className={composedMarkdownClassName}
            {...typographyProps}
            ref={dndRef}
          >
            <React.Suspense fallback={null}>
              <Markdown>{markdownSource}</Markdown>
            </React.Suspense>
          </Typography>,
        )}
        {tooltipBuilderNode}
      </>
    );
  }

  if ((resolvedType === 'RICH_TEXT' || resolvedType === 'HTML') && !_isNil(value)) {
    return (
      <>
        {wrapWithTooltip(<HtmlRenderer {...htmlProps} />)}
        {tooltipBuilderNode}
      </>
    );
  }

  if (enableTruncation && truncateWith === 'label' && maxNumberOfLines && !isNative()) {
    const text = getStringifiedValue(value);

    return (
      <React.Suspense fallback={null}>
        {wrapWithTooltip(
          <TypographyWithExpandCollapse
            className={clsx(className, colorClassName, textClassName, 'whitespace-pre-line')}
            ref={dndRef}
            text={text}
            {...typographyProps}
          >
            <TypographyWithExpandCollapse.Slot name={TypographyWithExpandCollapseSlots.Collapse}>
              {slots?.collapsedTypography?.blockId &&
              !isEmptyBlock(slots.collapsedTypography.blockId) ? (
                <Link style={LINK_STYLES}>
                  <BlockRenderer
                    blockId={slots.collapsedTypography.blockId}
                    key={slots.collapsedTypography.blockId}
                    parentBlockId={computedBlockState.id}
                  />
                </Link>
              ) : null}
            </TypographyWithExpandCollapse.Slot>
            <TypographyWithExpandCollapse.Slot name={TypographyWithExpandCollapseSlots.Expand}>
              {slots?.expandedTypography?.blockId &&
              !isEmptyBlock(slots.expandedTypography.blockId) ? (
                <Link style={LINK_STYLES}>
                  <BlockRenderer
                    blockId={slots.expandedTypography.blockId}
                    parentBlockId={computedBlockState.id}
                  />
                </Link>
              ) : null}
            </TypographyWithExpandCollapse.Slot>
          </TypographyWithExpandCollapse>,
        )}
        {tooltipBuilderNode}
      </React.Suspense>
    );
  }

  return (
    <>
      {wrapWithTooltip(plainText)}
      {tooltipBuilderNode}
    </>
  );
}

export default memo(TypographyBlock);
